<template>
    <div>
        <limitless-page-header :loading="!initialized && busy_loading" :sub-title="category_name" :title-id="category_id"></limitless-page-header>

        <div class="content" v-if="!initialized && busy_loading">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div class="content" v-else-if="!not_found && category">
            <form @submit.prevent="saveCategory">
                <ValidationObserver ref="categoryform">
                    <div class="card">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-6">
                                    <div class="form-group">
                                        <limitless-tabs
                                            :tabs="language_tabs"
                                        >
                                            <template v-for="lang in language_tabs" v-slot:[lang.slot]>
                                                <div :key="'tab-' + lang.id">
                                                    <ValidationProvider :vid="'translations.' + lang.id + '.categoryName'" name="Namn" v-slot="{ errors }" class="form-group" tag="div">
                                                        <label>Namn</label>
                                                        <input type="text" class="form-control" v-model="translations[lang.id].categoryName" v-bind:class="{ 'border-danger':errors[0] }">
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                    <ValidationProvider :vid="'translations.' + lang.id + '.categoryDescription'" name="Beskrivning" v-slot="{ errors }" class="form-group"  tag="div">
                                                        <label>Beskrivning</label>
                                                        <elastic-textarea
                                                            class="form-control"
                                                            v-model="translations[lang.id].categoryDescription"
                                                        ></elastic-textarea>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </template>
                                        </limitless-tabs>
                                    </div>

                                    <div class="form-group">
                                        <label>Kategoribild</label>
                                        <div style="max-width: 300px;">
                                            <mediabank-selector
                                                v-model="category.categoryImage"
                                                btn-class="btn-sm btn-secondary"
                                                btn-text="Välj kategoribild"
                                                btn-icon="icon-image2"
                                                :category-id="1015"
                                                category-locked
                                                thumbnail
                                                upload
                                            ></mediabank-selector>
                                        </div>
                                    </div>

                                    <ValidationProvider vid="category.type" name="Typ" rules="required" v-slot="{ errors }" class="form-group" tag="div">
                                        <label>Typ</label>
                                        <div class="border rounded p-2" v-bind:class="{ 'border-danger':errors[0] }">
                                            <div class="form-check" v-for="category_type in category_types" :key="'category-type-' + category_type.value">
                                                <label class="form-check-label" v-bind:class="{ 'text-muted': isCategoryTypeDisabled(category_type.value) }">
                                                    <input
                                                        type="radio"
                                                        class="form-check-input"
                                                        :value="category_type.value"
                                                        v-model="category.type"
                                                        :disabled="isCategoryTypeDisabled(category_type.value)"
                                                    >
                                                    {{ category_type.label }}
                                                </label>
                                            </div>
                                        </div>
                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </div> <!-- /col -->
                                <div class="col-lg-6">
                                    <div class="card">
                                        <div class="card-header bg-white header-elements-inline">
                                            <h5 class="card-title">Underkategorier</h5>
                                            <div class="header-elements">
                                                <button type="button" class="btn btn-light" @click="addSubCategory"><i class="icon-plus3 mr-2"></i>Lägg till</button>
                                            </div>
                                        </div>
                                        <div class="">
                                            <draggable v-model="sub_categories" handle=".handle" tag="ul" class="list-group list-group-bordered border-0">
                                                <li v-for="sub_cat in sub_categories" :key="'sc-' + sub_cat.SubCategoryID" class="list-group-item">
                                                    <input type="text" class="form-control" v-model="sub_cat.SubCategoryName">
                                                    <div class="handle pl-3"><i class="icon-arrow-resize8"></i></div>
                                                </li>
                                            </draggable>
                                        </div>
                                    </div>
                                </div><!-- /col -->
                            </div> <!-- /row -->
                        </div>
                        <div class="card-footer">
                            <div class="d-flex justify-content-end">
                                <limitless-button
                                    ref="btndeletecategory"
                                    type="button"
                                    @click="deleteCategory"
                                    btn-class="btn-danger"
                                    :busy="busy_deleting"
                                    icon="icon-trash"
                                    :disabled="busy_saving || category.StatusID >= 50"
                                    class="mr-auto"
                                >
                                    Radera
                                </limitless-button>
                                <limitless-button
                                    ref="btnsavecategory"
                                    type="button"
                                    @click="saveCategory"
                                    :busy="busy_saving"
                                    icon="icon-floppy-disk"
                                    :disabled="busy_deleting"
                                >
                                    SPARA
                                </limitless-button>
                            </div>
                        </div>
                    </div> <!-- /card -->
                </ValidationObserver>
            </form>

            <activity-notification v-if="category && category.ProdCatID > 0" :channel="'product-category-' + category.ProdCatID"></activity-notification>
        </div>
        <div class="content" v-else-if="not_found">
            <i>Kategorin hittades inte</i>
        </div>
        <div class="content" v-else>
            <i>Något gick fel...</i>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import ActivityNotification from '@components/User/ActivityNotification';
import ElasticTextarea from '@components/ElasticTextarea';
import {
    formatDateTime,
} from '@services/Helpers';
import getError from '@services/ErrorHelper';
import LimitlessTabs from '@components/Limitless/LimitlessTabs.vue';
import draggable from 'vuedraggable';
import MediabankSelector from '@components/MediabankSelector.vue';
import BackendRepository from '@services/repositories/BackendRepository';

export default {
    name: 'CategoryEditor',
    components: {
        MediabankSelector,
        draggable,
        LimitlessTabs,
        ElasticTextarea,
        LimitlessButton,
        LimitlessPageHeader,
        ActivityNotification,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        busy_deleting: false,
        category: null,
        sub_categories: [],
        translations: null,
        languages: [],
        category_types: [
            { value: 'category', label: 'Vanlig kategori' },
            { value: 'all', label: 'Alla produkter' },
            { value: 'popular', label: 'Utvalda favoriter' },
            { value: 'news', label: 'Nyheter' },
            { value: 'disabled', label: 'Inaktiverad' },
        ],
    }),
    created() {
        this.getLanguages();
        this.getCategory();
    },
    methods: {
        formatDateTime,
        async getCategory() {
            this.busy_loading = true;
            await apiClient.get('/vue/product_categories/' + this.id)
                .then((response) => {
                    this.category = response.data.category;
                    if (!this.category.type) {
                        this.category.type = 'category';
                    }
                    if (this.category_has_products) {
                        this.category.type = 'category';
                    }
                    this.sub_categories = response.data.sub_categories;
                    this.translations = response.data.translations;
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async saveCategory() {
            const isValid = await this.$refs.categoryform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/product_categories/' + this.id, {
                    category: { ...this.category },
                    translations: { ...this.translations },
                    sub_categories: { ...this.sub_categories },
                }).then((response) => {
                    if (parseInt(this.id, 10) !== parseInt(response.data.category.ProdCatID, 10)) {
                        window.location.href = '/products/categories/' + response.data.category.ProdCatID;
                    }
                    this.category = response.data.category;
                    this.sub_categories = response.data.sub_categories;
                    this.translations = response.data.translations;
                    this.$refs.btnsavecategory.triggerSaved();
                }).catch((error) => {
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        this.$refs.categoryform.setErrors(parsederror);
                    }
                    this.$refs.btnsavecategory.triggerFailed();
                }).finally(() => {
                    this.busy_saving = false;
                });
            } else {
                this.$refs.btnsavecategory.triggerFailed();
            }
        },
        async deleteCategory() {
            let doDelete = false;
            await this.$swal({
                text: 'Är du säker att du vill radera denna kategori?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, radera!',
            }).then((result) => {
                if (result.isConfirmed) {
                    doDelete = true;
                }
            });
            if (doDelete) {
                this.busy_deleting = true;
                await apiClient.delete('/vue/product_categories/' + this.id)
                    .then(() => {
                        window.location.href = '/products/categories';
                        this.$refs.btndeletecategory.triggerSaved();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.categoryform.setErrors(parsederror);
                        }
                        this.$refs.btndeletecategory.triggerFailed();
                    }).finally(() => {
                        this.busy_deleting = false;
                    });
            }
        },
        getLanguages() {
            BackendRepository.getLanguages().then((res) => {
                this.languages = res;
            });
        },
        addSubCategory() {
            this.sub_categories.push({
                SubCategoryName: '',
            });
        },
        isCategoryTypeDisabled(type) {
            return this.category_has_products && type !== 'category';
        },
    },
    computed: {
        category_has_products() {
            return this.category && parseInt(this.category.products_count, 10) > 0;
        },
        language_tabs() {
            return this.languages.map((lang) => ({
                id: lang.short.toLowerCase(),
                label: lang.name,
                slot: 'slot-' + lang.short,
            }));
        },
        category_name() {
            return (this.category && this.category.ProdCatID > 0 ? this.category.categoryName : 'Ny kategori');
        },
        category_id() {
            return (this.category && this.category.ProdCatID > 0 ? this.category.ProdCatID : '');
        },
    },
};
</script>

<style scoped lang="scss">
.sortable-ghost {
    border: 1px dashed #aaa;
    position: relative;
    box-shadow: none;
    -webkit-box-shadow: none;
}

.sortable-ghost:before {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #ffffff;
    z-index: 10;
    padding: 5px;
}
.handle {
    float: right;
    cursor: pointer;
}
</style>
